'use client'
import { type FC } from 'react'
import styled, { css } from 'styled-components'
import type { TypeCmsVariant } from '@/schema'
import { LANG } from '@/utils'
import { presets } from '@/styles'

type Props = {
  data: {
    stock: TypeCmsVariant['stock'],
    minSaleUnits: TypeCmsVariant['minSaleUnits'],
    stockWarning: TypeCmsVariant['stockWarning']
    frozen?: boolean
  }
  displayedIn?: 'card' | 'pdp' | 'cartline'
}

const TRANSLATIONS_LAST_UNITS = {
  en: 'Last units',
  es: 'Últimas unidades',
  fr: 'Dernières unités',
  de: 'Letzte Einheiten',
  pt: 'Últimas unidades',
  it: 'Ultime unità'
} as const

const TRANSLATIONS_SOLD_OUT = {
  en: 'Sold out',
  es: 'Agotado',
  fr: 'Épuisé',
  de: 'Ausverkauft',
  pt: 'Esgotado',
  it: 'Esaurito'
} as const

const TRANSLATIONS_NOT_AVAILABLE = {
  en: 'Not available',
  es: 'No disponible',
  fr: 'Indisponible',
  de: 'Nicht verfügbar',
  pt: 'Não disponível',
  it: 'Non disponibile'
} as const

export const StockWarning:FC<Props> = ({ data, displayedIn = 'card' }) => {
  const soldOut = typeof data.stock === 'number' && typeof data.minSaleUnits === 'number' && data.stock < data.minSaleUnits
  const notAvailable = typeof data.stock !== 'number' || typeof data.minSaleUnits !== 'number'
  const lastUnits = data.stockWarning && !soldOut && !notAvailable

  if (!soldOut && !notAvailable && !lastUnits) return null

  const dataType = notAvailable ? 'not-available' : soldOut ? 'sold-out' : lastUnits ? 'last-units' : ''

  return (
    <P className='stock-warning' aria-live='polite' data-type={dataType} $displayedIn={displayedIn} $frozen={data?.frozen}>
      {notAvailable ? TRANSLATIONS_NOT_AVAILABLE[LANG] : soldOut ? TRANSLATIONS_SOLD_OUT[LANG] : lastUnits ? TRANSLATIONS_LAST_UNITS[LANG] : ''}
    </P>
  )
}

const CssLastUnitsCard = css`
  margin-top: auto;

  + .price {
    margin-top: 2px;
  }
`

const CssLastUnitsDetailPage = css`
  margin-top: auto;
`

const CssSoldOutCard = css`
  bottom: 8px;
  color: var(--color-white);
  padding: 5px 10px;
  position: absolute;
  right: 8px;
`

const CssSoldOutDetailPage = css`
	${presets.font15()}
	padding: 5px 10px;
	font-weight: 500;
`

const P = styled.p<{ $displayedIn: Props['displayedIn'], $frozen?: boolean }>`
  ${presets.font12()}
  padding: 0;

  &[data-type='last-units'] {
    ${({ $displayedIn }) => $displayedIn === 'card' ? CssLastUnitsCard : $displayedIn === 'pdp' ? CssLastUnitsDetailPage : ''}
  }

  &[data-type='sold-out'] {
    background-color: ${({ $frozen }) =>  `var(--color-card-button-bg--disabled${$frozen ? '--frozen' : ''})`};
    border-radius: 30px;
    color: var(--color-white);
    ${({ $displayedIn }) => $displayedIn === 'card' ? CssSoldOutCard : $displayedIn === 'pdp' ? CssSoldOutDetailPage : ''}
  }
`
